import distance from './distance';

type LocationLike = {
  id?: string | number;
  pantry_id?: number;
  name?: string;
  latitude?: number | null;
  longitude?: number | null;
};

const DEDUP_RADIUS_KM = 0.05;

function approxKey(lat: number, lng: number): string {
  return `${Math.round(lat * 1000)}:${Math.round(lng * 1000)}`;
}

function neighborKeys(lat: number, lng: number): string[] {
  const baseLat = Math.round(lat * 1000);
  const baseLng = Math.round(lng * 1000);
  const keys: string[] = [];
  for (let dlat = -1; dlat <= 1; dlat += 1) {
    for (let dlng = -1; dlng <= 1; dlng += 1) {
      keys.push(`${baseLat + dlat}:${baseLng + dlng}`);
    }
  }
  return keys;
}

function normalizeName(name: string | undefined): string {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function nameSimilar(a: string | undefined, b: string | undefined): boolean {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  return na.includes(nb) || nb.includes(na);
}

export type MergeOptions = {
  /** Proximity below which a secondary row is a candidate duplicate. */
  radiusKm?: number;
  /** Require the names to look similar before treating rows as duplicates. */
  requireNameMatch?: boolean;
};

/**
 * Merge two location lists, with the PRIMARY list winning on conflict. A
 * secondary row is dropped when it sits within `radiusKm` of a primary row
 * (and, when `requireNameMatch`, their names also look similar). Distinct
 * pantries that share a building keep both pins whenever a name match is
 * required.
 *
 * Defaults reproduce the original Lighthouse behaviour (~50m + name match).
 * The FreshTrak overlay passes primary = FreshTrak so its green pins win over
 * coincident Plentiful pins, with a tighter 20m radius and geo-only matching
 * (FreshTrak and Plentiful name the same site differently).
 */
export function mergeLocations<T extends LocationLike>(
  primaryRows: T[],
  secondaryRows: T[],
  opts: MergeOptions = {}
): T[] {
  const radiusKm = opts.radiusKm ?? DEDUP_RADIUS_KM;
  const requireNameMatch = opts.requireNameMatch ?? true;

  // Bucket primary rows into a coarse ~111m grid so each secondary row only
  // compares against the primaries in its own and adjacent cells (O(n) rather
  // than O(n*m)).
  const index = new Map<string, T[]>();
  primaryRows.forEach((row) => {
    if (row.latitude == null || row.longitude == null) return;
    const key = approxKey(row.latitude, row.longitude);
    const bucket = index.get(key);
    if (bucket) bucket.push(row);
    else index.set(key, [row]);
  });

  const isDuplicate = (sec: T): boolean => {
    const { latitude: secLat, longitude: secLng } = sec;
    if (secLat == null || secLng == null) return false;
    return neighborKeys(secLat, secLng).some((neighborKey) =>
      (index.get(neighborKey) ?? []).some(
        (p) =>
          p.latitude != null &&
          p.longitude != null &&
          distance(p.latitude, p.longitude, secLat, secLng, 'K') <= radiusKm &&
          (!requireNameMatch || nameSimilar(p.name, sec.name))
      )
    );
  };

  // Primary rows always survive; secondary rows survive unless a primary
  // already covers the same spot. Secondary rows without coords can't be
  // matched geographically, so they pass through.
  return [...primaryRows, ...secondaryRows.filter((sec) => !isDuplicate(sec))];
}

export default mergeLocations;
